<script setup lang="ts">
import {
  ref,
  nextTick,
  onMounted,
  onUnmounted,
  watch,
  computed,
  type ComponentPublicInstance,
} from 'vue';
import TreeTable from 'primevue/treetable';
import Column from 'primevue/column';
import type { TreeNode } from 'primevue/treenode';

import { IconFilter } from '@/components/design/icons';
import type {
  AndCriterionInput,
  AndCriterionOutput,
  GaiaJsonValue,
  ContractManagementContractFileType,
  ContractManagementContractStates,
  ContractManagementExtractionInDb,
  EffectiveState,
} from '@/api';
import {
  hasActiveFilters,
  extractFilterValues,
} from '@/utils/contractManagementView/staticFilters';
import { formatDisplayValue } from '@/utils/displayAiResponse';
import ExtractionCellState from '@/components/contractManagement/ExtractionCellState.vue';
import GaiaContractStateCell from '@/components/design/domain/contractManagement/GaiaContractStateCell.vue';
import { STATIC_COLUMN_CONFIGS } from '@/types/contractManagement/viewTypes';
import { fileTypeHasState } from '@/components/design/domain/contractManagement/contractStateIcons';
import {
  gaiaAccent,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaStatus,
  gaiaTracking,
} from '@/components/design/themes/tokens';
import { logger } from '@/utils/logger';
import { safeStringify } from '@/utils/jsonHelpers';

/**
 * The state column's grid field. A contract row holds its
 * `{contract_state, amendment_state}`, a file row the file's own state.
 */
const STATE_FIELD = STATIC_COLUMN_CONFIGS.state.fieldKey;

// --- Types ---
export interface ColumnDef {
  field: string;
  headerName: string;
  width?: number; // The preferred width
  minWidth?: number; // The absolute minimum
  maxWidth?: number;
  expander?: boolean;
  frozen?: boolean;
  alignFrozen?: 'left' | 'right';
  isAddColumn?: boolean;
  isRowNumber?: boolean;
  isStatus?: boolean;
  isStaticColumn?: boolean; // True for static columns (contractual parties, matter, owner, …)
  /**
   * The key the backend sorts this column by — `created_at`, `type`, …
   * Set it to make the header sortable; the field name can't stand in because
   * static columns carry synthetic fields (`__static_matter`).
   */
  sortKey?: string;
  filters?: AndCriterionInput | AndCriterionOutput | null; // Static filters for this column
  responseType?: string | object; // Response type for formatting display values
}

export type AnyTreeNode = TreeNode & {
  data?: Record<string, unknown>;
};

// --- Props ---
const props = defineProps<{
  rowData: (Record<string, unknown> | AnyTreeNode)[];
  columnDefs?: ColumnDef[];
  treeByField?: string;
  /** If true, enables preview mode: hides "Add Column" and disables context menu */
  previewMode?: boolean;
  /** Column currently sorted, as its `sortKey`. */
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  /**
   * The user has actually clicked a header. The table arrives sorted by
   * `created_at desc` — drawing a caret for that would claim a choice nobody
   * made. Same contract as GaiaDataTable.
   */
  sortActive?: boolean;
}>();

// --- Slots ---
defineSlots<{
  /** Slot for rendering empty cells in dynamic columns. Receives node, field, and columnDef. */
  'empty-cell'?: (props: {
    node: AnyTreeNode;
    field: string;
    columnDef: ColumnDef | undefined;
  }) => unknown;
}>();

// --- Emits ---
const emit = defineEmits<{
  (e: 'add-column'): void;
  (e: 'node-expand', nodeKey: string | number): void;
  (
    e: 'edit-cell',
    data: { nodeKey: string | number; field: string; cellData: Record<string, unknown> }
  ): void;
  (e: 'go-to-contract', data: { contractUid: string; nodeKey: string | number | undefined }): void;
  (e: 'edit-column-filters', data: { field: string; isStaticColumn: boolean }): void;
  (
    e: 'go-to-contract-file',
    data: { contractFileUid: string; contractUid: string; nodeKey: string | number | undefined }
  ): void;
  (e: 'add-revision', data: { vaultFileUid: string; vaultFileName: string }): void;
  (e: 'sort', sortKey: string): void;
}>();

// --- Internal State ---
const contextMenuVisible = ref(false);
const contextMenuPosition = ref({ x: 0, y: 0 });
const contextMenuCellData = ref<{
  value: unknown;
  column: string;
  rowData: Record<string, unknown> | null;
} | null>(null);
const contextMenuRef = ref<HTMLElement | null>(null);

const expandedKeys = ref<Record<string, boolean>>({});

// Excel-like selection/editing
const selectedCell = ref<{ key: string | number; field: string } | null>(null);
const editingCell = ref<{ key: string | number; field: string } | null>(null);
const editingValue = ref<string>('');

// --- Column Definitions ---
// A compact trailing "+" rather than a full-width column: the mock keeps it to
// the width of the glyph so it reads as an affordance on the header row, not as
// another column of data.
const addColumnDef: ColumnDef = {
  headerName: '+',
  field: '__addColumn',
  width: 44,
  minWidth: 44,
  isAddColumn: true,
};

const defaultColumnDefs: ColumnDef[] = [
  {
    headerName: '#',
    field: '__rowNumber',
    width: 60,
    minWidth: 60,
    frozen: true,
    alignFrozen: 'left',
    isRowNumber: true,
  },
  {
    field: 'name',
    headerName: 'Name',
    width: 300,
    minWidth: 200,
    expander: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    isStatus: true,
    width: 150,
    minWidth: 120,
  },
];

const ensureExpander = (defs: ColumnDef[]): ColumnDef[] => {
  const cloned = defs.map(d => ({ ...d }));
  const hasExpander = cloned.some(c => c.expander);

  if (!hasExpander) {
    const nameCol = cloned.find(c => c.field === 'name');
    const target = nameCol ?? cloned.find(c => !c.isRowNumber && !c.isAddColumn) ?? cloned.at(0);
    if (target) target.expander = true;
  }
  return cloned;
};

const getColumnDefsWithAdd = (defs: ColumnDef[]): ColumnDef[] => {
  const withExpander = ensureExpander(defs);
  // If previewMode is true, don't add the add column
  if (props.previewMode) {
    return withExpander;
  }
  const hasAddCol = withExpander.some(col => col.field === '__addColumn');
  if (hasAddCol) return withExpander;
  return [...withExpander, addColumnDef];
};

const columnDefs = ref<ColumnDef[]>(getColumnDefsWithAdd(props.columnDefs ?? defaultColumnDefs));

// Create a Map for O(1) lookup of column definitions by field name
// This avoids O(n) find() calls on every cell render
const columnDefsMap = computed(() => {
  const map = new Map<string, ColumnDef>();
  for (const col of columnDefs.value) {
    map.set(col.field, col);
  }
  return map;
});

watch(
  () => props.columnDefs,
  newDefs => {
    if (newDefs) {
      columnDefs.value = getColumnDefsWithAdd(newDefs);
    }
  },
  { deep: true }
);

const columnsWithAdd = computed(() => columnDefs.value);

// --- Chrome ---
// PrimeVue's TreeTable owns its own layout and paints through `:deep`, so its
// type and colour arrive by v-bind rather than through Gaia components.
const cellSize = gaiaFontSize.dense;
const cellColor = gaiaNeutral.text;
const inkColor = gaiaNeutral.ink;
const faintColor = gaiaNeutral.textMuted;
const blankColor = gaiaNeutral.textBlank;
const microSize = gaiaFontSize.micro;
const headSurface = gaiaNeutral.surfaceSoft;
const headColor = gaiaNeutral.textSubtle;
const headSize = gaiaFontSize.nano;
const headWeight = String(gaiaFontWeight.bold);
const headTracking = gaiaTracking.label;
const headBorder = gaiaNeutral.borderDivider;
const cellBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
const surface = gaiaNeutral.surface;
const accent = gaiaAccent.base;
const accentSurface = gaiaAccent.surface;
const alertColor = gaiaStatus.alert;
const alertSurface = gaiaStatus.alertSurface;

// --- Helpers ---
const statusClass = (status?: string): string => {
  switch (status) {
    case 'Active':
    case 'Cleared':
    case 'Passed':
      return 'status-active';
    case 'Prospect':
    case 'Negotiation':
      return 'status-prospect';
    case 'Churned':
    case 'Overdue':
      return 'status-churned';
    case 'Trial':
    case 'Pending':
      return 'status-trial';
    case 'Review':
      return 'status-trial';
    default:
      return '';
  }
};

// Helper function to find parent contract node in the tree
const findParentContractInTree = (
  node: AnyTreeNode,
  allNodes: AnyTreeNode[]
): AnyTreeNode | null => {
  // If this node has a contractUid in its data, find the parent
  if (node.data?.contractUid) {
    const parentKey = node.data.contractUid as string;
    const findNode = (nodes: AnyTreeNode[]): AnyTreeNode | null => {
      for (const n of nodes) {
        if (n.key === parentKey) {
          return n;
        }
        if (n.children) {
          const found = findNode(n.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findNode(allNodes);
  }
  return null;
};

// Helper function to check if a file's column value matches the contract's value
// Returns: 'most-recent-value' if matches, 'faded-value' if different or null, '' if not a file node or name field
const getColumnValueClass = (node: AnyTreeNode, field: string): string => {
  // Don't apply highlighting to the name field - it should always be normal
  if (field === 'name') {
    return '';
  }

  // Only apply highlighting to file nodes (children), not contract nodes
  // A node is a file if it has a contractUid linking it to a parent
  const isFileNode = Boolean(node.data?.contractUid);

  if (!isFileNode) {
    // This is a parent node (contract), no highlighting needed
    return '';
  }

  // This is a file node, check if it has a parent contract
  const parentContract = findParentContractInTree(node, treeData.value);
  if (!parentContract) {
    return '';
  }

  const fileValue = node.data?.[field];
  const contractValue = parentContract.data?.[field];

  // Check if file has a value (not null, undefined, or empty string)
  const hasValue = fileValue !== null && fileValue !== undefined && fileValue !== '';

  // Check if contract has a value
  const contractHasValue =
    contractValue !== null && contractValue !== undefined && contractValue !== '';

  // If both have values and they match, highlight as most recent
  if (hasValue && contractHasValue && String(fileValue) === String(contractValue)) {
    return 'most-recent-value';
  }

  // If file has no value (null) or has a different value, fade it
  // This covers: different value, null value, or contract has no value
  return 'faded-value';
};

// Helper function to format the display value for a cell
// Returns the value or "—" if null/undefined/empty (for file nodes only)
/** Cheap blank test on the raw value — used to tint the em-dash without
 *  running the formatter a second time for every cell. */
const isBlankCell = (node: AnyTreeNode, field: string): boolean => {
  const value = node.data?.[field];
  return value === null || value === undefined || value === '';
};

const getColumnDisplayValue = (node: AnyTreeNode, field: string): string => {
  const value = node.data?.[field];

  // An empty cell reads as an em-dash rather than a hole in the row — it says
  // "no value" instead of looking like something failed to render.
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  // Use Map for O(1) lookup instead of O(n) find()
  const columnDef = columnDefsMap.value.get(field);
  const responseType = columnDef?.responseType;

  // Use formatDisplayValue for proper formatting of special types
  // Allow objects (CurrencyAmount, Entity, etc.) - formatDisplayValue handles them
  if (value != null) {
    return formatDisplayValue(value as GaiaJsonValue, responseType);
  }

  return '';
};

// Shape of extraction metadata stored on node.data (set by DetailView)
interface ExtractionCellMetadata {
  extraction: ContractManagementExtractionInDb | null;
}

/** Key used on node.data for extraction metadata. Must match DetailView. */
const getExtractionMetaKey = (field: string): string => `__extraction_${field}`;

/** Get extraction metadata for a cell (single source of truth for metadata access). */
const getExtractionMetadata = (
  node: AnyTreeNode,
  field: string
): ExtractionCellMetadata | undefined =>
  node.data?.[getExtractionMetaKey(field)] as ExtractionCellMetadata | undefined;

/** Extraction object for the cell, or null if none / not created yet. */
const getExtractionForCell = (
  node: AnyTreeNode,
  field: string
): ContractManagementExtractionInDb | null =>
  getExtractionMetadata(node, field)?.extraction ?? null;

/** True if this column is a dynamic (extraction) column that can show extraction state. */
const isDynamicExtractionColumn = (columnDef: ColumnDef | undefined, field: string): boolean => {
  if (!columnDef || field === 'name') return false;
  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- boolean OR chain, not nullish fallbacks */
  return !(
    columnDef.isRowNumber ||
    columnDef.isAddColumn ||
    columnDef.isStatus ||
    columnDef.isStaticColumn
  );
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
};

/** True if the cell should render ExtractionCellState (or empty-cell slot in preview). */
const shouldShowExtractionState = (node: AnyTreeNode, field: string): boolean => {
  if (!isDynamicExtractionColumn(columnDefsMap.value.get(field), field)) return false;
  const meta = getExtractionMetadata(node, field);
  if (meta != null) return true;
  // Fallback when metadata wasn't set (e.g. older code paths)
  const value = node.data?.[field];
  return value === null || value === undefined || value === '';
};

// Normalize rowData
const treeData = computed<AnyTreeNode[]>(() => {
  const input = props.rowData;
  if (input.length === 0) return [];

  const first = input[0];

  if (typeof first === 'object' && ('children' in first || ('key' in first && 'data' in first))) {
    return input as AnyTreeNode[];
  }

  const field = props.treeByField ?? 'status';
  const groups: Record<string, AnyTreeNode> = {};
  let counter = 0;

  for (const row of input as Record<string, unknown>[]) {
    const groupKey = row[field] == null ? 'Ungrouped' : safeStringify(row[field]);
    if (!(groupKey in groups)) {
      groups[groupKey] = {
        key: groupKey,
        data: {
          [field]: groupKey,
          name: groupKey,
        },
        children: [],
      };
    }

    counter += 1;
    const childNode: AnyTreeNode = {
      key: `${groupKey}-${counter}`,
      data: {
        ...row,
        __rowNumber: counter,
      },
    };
    (groups[groupKey].children ?? (groups[groupKey].children = [])).push(childNode);
  }

  return Object.values(groups);
});

const initExpandedKeys = (): void => {
  // Initialize with empty keys so all nodes are collapsed by default
  expandedKeys.value = {};
};

// Watch for expansion changes and emit events
// Handle node expansion directly from the component event
// PrimeVue TreeTable node-expand event can pass either:
// 1. An object with a `node` property: { node: TreeNode }
// 2. The node directly: TreeNode
const onNodeExpand = (event: unknown): void => {
  let node: AnyTreeNode | undefined;

  // Check if event has a node property (PrimeVue wrapped format)
  if (event && typeof event === 'object' && 'node' in event) {
    const eventWithNode = event as { node: AnyTreeNode };
    node = eventWithNode.node;
  }
  // Otherwise, assume event is the node itself
  else if (event && typeof event === 'object' && 'key' in event) {
    node = event as AnyTreeNode;
  }

  logger.info('onNodeExpand triggered:', node);

  if (node && node.key) {
    emit('node-expand', node.key);
  }
};

// --- Selection / Editing ---
const isCellSelected = (node: AnyTreeNode, field: string): boolean => {
  return (
    selectedCell.value != null &&
    selectedCell.value.key === node.key &&
    selectedCell.value.field === field
  );
};

const isCellEditing = (node: AnyTreeNode, field: string): boolean => {
  return (
    editingCell.value != null &&
    editingCell.value.key === node.key &&
    editingCell.value.field === field
  );
};

/**
 * A cell shows one ellipsised line, so clicking it opens the panel that carries
 * the value in full plus whatever can be done with it. This is the same panel
 * right-click used to raise — the actions just aren't hidden behind a gesture
 * nobody discovers any more.
 */
const handleCellClick = (node: AnyTreeNode, field: string, event: MouseEvent): void => {
  if (field === '__rowNumber' || field === '__addColumn') return;
  selectedCell.value = { key: node.key as string | number, field };
  openCellPanel(node, field, event);
};

/** Anchor the panel under the cell rather than at the pointer, so repeated
 *  clicks on the same cell put it in the same place. */
const openCellPanel = (node: AnyTreeNode, field: string, event: MouseEvent): void => {
  if (props.previewMode) return;

  const cell = event.currentTarget;
  // The anchor's top edge rides along so the clamp below can flip the panel
  // ABOVE the cell when there is no room under it (a bottom-row cell used to
  // push the panel clean off the screen).
  let anchorTop: number;
  if (cell instanceof HTMLElement) {
    const rect = cell.getBoundingClientRect();
    contextMenuPosition.value = { x: rect.left, y: rect.bottom + 4 };
    anchorTop = rect.top;
  } else {
    contextMenuPosition.value = { x: event.clientX, y: event.clientY };
    anchorTop = event.clientY;
  }

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
  const rowData = (node.data || {}) as Record<string, unknown>;
  contextMenuCellData.value = { value: rowData[field], column: field, rowData };
  contextMenuVisible.value = true;

  // The panel is position:fixed and its size is unknown until it renders, so
  // the clamp runs one tick later: flip above the anchor when the bottom edge
  // would leave the viewport, and pull the left edge in when the right would.
  void nextTick(() => {
    const panel = contextMenuRef.value;
    if (!panel) return;
    const box = panel.getBoundingClientRect();
    const margin = 8;
    let { x, y } = contextMenuPosition.value;
    if (y + box.height > window.innerHeight - margin) {
      y = Math.max(margin, anchorTop - box.height - 4);
    }
    if (x + box.width > window.innerWidth - margin) {
      x = Math.max(margin, window.innerWidth - box.width - margin);
    }
    contextMenuPosition.value = { x, y };
  });
};

// The editor lives inside a v-for'd column body slot, so a named ref would
// collect into an array. A function ref focuses the one editor that mounts —
// this replaces the `autofocus` attribute, which the a11y linter disallows.
const focusCellEditor = (el: Element | ComponentPublicInstance | null): void => {
  if (el instanceof HTMLInputElement) el.focus();
};

const handleCellDblClick = (node: AnyTreeNode, field: string): void => {
  if (field === '__addColumn' || field === '__rowNumber') return;
  selectedCell.value = { key: node.key as string | number, field };
  editingCell.value = { key: node.key as string | number, field };
  editingValue.value = String((node.data ?? {})[field] ?? '');
};

const commitEdit = (node: AnyTreeNode, field: string): void => {
  if (!isCellEditing(node, field)) return;
  if (node.data) {
    (node.data as Record<string, unknown>)[field] = editingValue.value;
  }
  editingCell.value = null;
};

// --- Cell panel ---
/** Right-click still raises the same panel — it just isn't the only way in. */
const onCellContextMenu = (node: AnyTreeNode, field: string, event: MouseEvent): void => {
  if (props.previewMode || field === '__addColumn' || field === '__rowNumber') return;
  event.preventDefault();
  event.stopPropagation();
  selectedCell.value = { key: node.key as string | number, field };
  openCellPanel(node, field, event);
};

/** The column's own header text — the panel leads with it so a value read out
 *  of context still says which column it came from. */
const panelColumnLabel = computed(() => {
  const field = contextMenuCellData.value?.column;
  if (!field) return '';
  return columnDefsMap.value.get(field)?.headerName ?? field;
});

const panelValue = computed(() => {
  const data = contextMenuCellData.value;
  if (!data) return '';
  const columnDef = columnDefsMap.value.get(data.column);
  if (data.value === null || data.value === undefined || data.value === '') return '—';
  return formatDisplayValue(data.value, columnDef?.responseType);
});

const panelValueIsBlank = computed(() => panelValue.value === '—');

/** Static columns hold assigned entities, so their edit action says so. */
const isStaticColumnCell = computed(() => {
  const field = contextMenuCellData.value?.column;
  return Boolean(field && columnDefsMap.value.get(field)?.isStaticColumn);
});

// Check if the current row is a contract file row
const isContractFileRow = computed(() => {
  if (!contextMenuCellData.value?.rowData) return false;
  const rowData = contextMenuCellData.value.rowData;
  // Contract file rows have contractUid that differs from uid
  return Boolean(rowData.contractUid) && rowData.contractUid !== rowData.uid;
});

// Check if the current contract file row has a vault file attached
const hasVaultFile = computed(() => {
  if (!contextMenuCellData.value?.rowData) return false;
  const rowData = contextMenuCellData.value.rowData;
  return Boolean(rowData.vaultFileUid);
});

// Check if edit should be enabled for the current cell
const isEditEnabled = computed(() => {
  if (!contextMenuCellData.value) return true;
  const field = contextMenuCellData.value.column;
  const rowData = contextMenuCellData.value.rowData;
  const columnDef = columnDefsMap.value.get(field);

  // Disable editing for Name column
  if (field === 'name') {
    return false;
  }

  // For static columns (contractual parties, matter, created at, type, tags),
  // only allow editing on contract files (not contract cells)
  if (columnDef?.isStaticColumn) {
    const isContractFile = Boolean(rowData?.contractUid);
    return isContractFile;
  }

  // For non-static columns, always allow editing
  return true;
});

// Get tooltip message for disabled edit option
const editDisabledTooltip = computed(() => {
  if (!contextMenuCellData.value || isEditEnabled.value) return '';

  const field = contextMenuCellData.value.column;
  const columnDef = columnDefsMap.value.get(field);

  // For Name column
  if (field === 'name') {
    return 'Name column cannot be edited';
  }

  // For any static column on contract cells
  if (columnDef?.isStaticColumn) {
    return 'You can edit this column only from contract files';
  }

  return '';
});

// The old menu's "Run" and "Delete" entries were both bodies that only closed
// the menu — they never did anything. They are not carried over rather than
// re-skinned into buttons that still do nothing; the mock's panel has neither.

const handleEditAction = (): void => {
  if (contextMenuCellData.value && contextMenuCellData.value.rowData) {
    const field = contextMenuCellData.value.column;

    // Find the node that contains this cell data
    const node = treeData.value.find(n => {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
      const nodeData = (n.data || {}) as Record<string, unknown>;
      return nodeData[field] !== undefined;
    });

    if (node) {
      emit('edit-cell', {
        nodeKey: node.key,
        field,
        cellData: contextMenuCellData.value.rowData,
      });
    }
  }
  contextMenuVisible.value = false;
};
const handleGoToContractAction = (): void => {
  if (contextMenuCellData.value && contextMenuCellData.value.rowData) {
    const rowData = contextMenuCellData.value.rowData;

    // Check if this is a contract file row (has contractUid property)
    const isContractFileRow = Boolean(rowData.contractUid) && rowData.contractUid !== rowData.uid;

    if (isContractFileRow) {
      // This is a contract file row - navigate to contract file detail
      const contractFileUid = rowData.uid as string | undefined;
      const contractUid = rowData.contractUid as string | undefined;
      const nodeKey = rowData.uid as string | number | undefined;

      if (contractFileUid && contractUid) {
        emit('go-to-contract-file', {
          contractFileUid,
          contractUid,
          nodeKey,
        });
      }
    } else {
      // This is a contract row - navigate to contract detail (existing behavior)
      const contractUid = rowData.uid as string | undefined;
      const nodeKey = rowData.uid as string | number | undefined;

      if (contractUid) {
        emit('go-to-contract', {
          contractUid,
          nodeKey,
        });
      }
    }
  }
  contextMenuVisible.value = false;
};
const handleAddRevisionAction = (): void => {
  if (contextMenuCellData.value?.rowData) {
    const rowData = contextMenuCellData.value.rowData;
    const vaultFileUid = rowData.vaultFileUid as string | undefined;
    const vaultFileName = (rowData.vaultFileName as string) || (rowData.name as string) || '';
    if (vaultFileUid) {
      emit('add-revision', { vaultFileUid, vaultFileName });
    }
  }
  contextMenuVisible.value = false;
};
const handleClickOutside = (event: MouseEvent): void => {
  if (!contextMenuVisible.value) return;

  // Check if the click is outside the context menu
  const target = event.target as HTMLElement;
  if (contextMenuRef.value && !contextMenuRef.value.contains(target)) {
    contextMenuVisible.value = false;
  }
};

// --- Add Column Logic ---
const handleAddColumn = (): void => {
  emit('add-column');
};

// --- Sorting ---
/**
 * Drawn as paths in GaiaDataTable's 10px box. Idle is the same two chevrons the
 * active carets are built from, minus the stem — so the column that gets sorted
 * doesn't sprout a new glyph, it just commits the one already there to a
 * direction.
 */
const IDLE_CARET = 'M2.5 4 5 1.5 7.5 4M2.5 6 5 8.5 7.5 6';
const ASC_CARET = 'M5 8.5v-7M2.5 4 5 1.5 7.5 4';
const DESC_CARET = 'M5 1.5v7M2.5 6 5 8.5 7.5 6';

function isSortedBy(col: ColumnDef): boolean {
  return Boolean(col.sortKey) && props.sortActive && props.sortKey === col.sortKey;
}

function sortCaret(col: ColumnDef): string {
  if (!isSortedBy(col)) return IDLE_CARET;
  return props.sortDirection === 'asc' ? ASC_CARET : DESC_CARET;
}

function handleHeaderSort(col: ColumnDef): void {
  if (!col.sortKey) return;
  emit('sort', col.sortKey);
}

// Watcher for treeData removed to prevent collapsing nodes when children are loaded
// expandedKeys is initialized to {} by default

onMounted(() => {
  // Use capture phase to catch clicks before they bubble
  document.addEventListener('click', handleClickOutside, true);
  initExpandedKeys();
});
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true);
});
</script>

<template>
  <div class="grid-container">
    <TreeTable
      v-model:expanded-keys="expandedKeys"
      class="excel-tree-table clay-custom-theme"
      :value="treeData"
      scrollable
      scroll-height="flex"
      :resizable-columns="true"
      column-resize-mode="expand"
      show-gridlines
      table-style="min-width: 100%; width: fit-content"
      @node-expand="onNodeExpand"
    >
      <Column
        v-for="col in columnsWithAdd"
        :key="col.field"
        :field="col.field"
        :expander="col.expander"
        :frozen="col.frozen"
        :align-frozen="col.alignFrozen"
        :style="{
          width: col.width ? col.width + 'px' : '200px', // Fallback width
          minWidth: col.minWidth ? col.minWidth + 'px' : undefined,
          maxWidth: col.maxWidth ? col.maxWidth + 'px' : undefined,
          flex: '0 0 auto', // CRITICAL: Prevents flexbox shrinking
        }"
      >
        <template #header>
          <div v-if="col.isAddColumn" class="add-column-header" @click.stop="handleAddColumn">
            {{ col.headerName }}
          </div>
          <span v-else-if="col.isRowNumber" class="row-number-header">
            {{ col.headerName }}
          </span>
          <div
            v-else
            class="treetable-header-text"
            :class="{
              'expander-indent': col.expander,
              sortable: Boolean(col.sortKey),
              sorted: isSortedBy(col),
            }"
            style="display: flex; align-items: center; gap: 6px; min-width: 0; overflow: hidden"
            @click="handleHeaderSort(col)"
          >
            <!-- Title with truncation and tooltip -->
            <NTooltip trigger="hover" placement="top">
              <template #trigger>
                <span
                  class="treetable-header-title"
                  style="
                    flex: 1;
                    min-width: 0;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  "
                >
                  {{ col.headerName }}
                </span>
              </template>
              <span style="font-size: 12px">{{ col.headerName }}</span>
            </NTooltip>

            <!-- Sort caret — carried by every sortable column so the
                 affordance is visible before anyone clicks. It stays faint
                 until this column is the one being sorted. -->
            <svg
              v-if="col.sortKey"
              class="sort-caret"
              :class="{ active: isSortedBy(col) }"
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              style="flex-shrink: 0"
            >
              <path :d="sortCaret(col)" />
            </svg>

            <!-- Filter icon and badge - always visible, never shrinks -->
            <NTooltip
              v-if="extractFilterValues(col.filters).length > 0"
              trigger="hover"
              placement="top"
            >
              <template #trigger>
                <div
                  class="treetable-header-filter"
                  style="
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    cursor: pointer;
                    flex-shrink: 0;
                  "
                  @click.stop="
                    emit('edit-column-filters', {
                      field: col.field,
                      isStaticColumn: col.isStaticColumn || false,
                    })
                  "
                >
                  <NIcon :size="14" :color="accent">
                    <IconFilter />
                  </NIcon>
                  <NBadge
                    :value="extractFilterValues(col.filters).length"
                    type="info"
                    :max="99"
                    style="font-size: 10px"
                  />
                </div>
              </template>
              <span style="font-size: 12px">
                Filter active: {{ extractFilterValues(col.filters).length }}
                {{ extractFilterValues(col.filters).length === 1 ? 'value' : 'values' }}
              </span>
            </NTooltip>
            <div
              v-else-if="hasActiveFilters(col.filters)"
              class="treetable-header-filter"
              style="display: flex; align-items: center; gap: 4px; cursor: pointer; flex-shrink: 0"
              @click.stop="
                emit('edit-column-filters', {
                  field: col.field,
                  isStaticColumn: col.isStaticColumn || false,
                })
              "
            >
              <NIcon :size="14" :color="accent">
                <IconFilter />
              </NIcon>
            </div>
          </div>
        </template>

        <template #body="{ node }">
          <div
            class="cell-wrapper"
            :class="{
              'cell-selected': isCellSelected(node, col.field),
              'cell-editing': isCellEditing(node, col.field),
              'cell-readonly': col.isRowNumber,
            }"
            @click.stop="handleCellClick(node, col.field, $event)"
            @dblclick.stop="handleCellDblClick(node, col.field)"
            @contextmenu.prevent.stop="onCellContextMenu(node, col.field, $event)"
          >
            <span v-if="col.isRowNumber" class="row-number-cell">
              {{ node.data?.__rowNumber ?? '' }}
            </span>

            <span
              v-else-if="col.isStatus && !isCellEditing(node, col.field)"
              class="status-pill"
              :class="statusClass(node.data?.[col.field] as string | undefined)"
            >
              {{ node.data?.[col.field] }}
            </span>

            <template v-else-if="isCellEditing(node, col.field)">
              <input
                :ref="focusCellEditor"
                v-model="editingValue"
                class="cell-editor-input"
                @keyup.enter.prevent="commitEdit(node, col.field)"
                @blur="commitEdit(node, col.field)"
              />
            </template>

            <template v-else-if="col.field === STATE_FIELD">
              <!-- A file row; appendices take no part in the contract's state. -->
              <template v-if="node.data?.contractUid">
                <gaia-contract-state-cell
                  v-if="
                    fileTypeHasState(node.data?.type as ContractManagementContractFileType | null)
                  "
                  :state="(node.data?.[col.field] as EffectiveState | null) ?? null"
                  :failure-reason="(node.data?.signatureFailureReason as string | null) ?? null"
                  compact
                />
              </template>
              <gaia-contract-state-cell
                v-else
                :states="
                  (node.data?.[col.field] as ContractManagementContractStates | null) ?? null
                "
              />
            </template>

            <!-- Extraction state: for dynamic columns. In preview with no extraction, show empty-cell slot (icon + tooltip). -->
            <template v-else-if="shouldShowExtractionState(node, col.field)">
              <template v-if="previewMode && getExtractionForCell(node, col.field) == null">
                <slot
                  name="empty-cell"
                  :node="node"
                  :field="col.field"
                  :column-def="columnDefsMap.get(col.field)"
                />
              </template>
              <ExtractionCellState
                v-else
                :extraction="getExtractionForCell(node, col.field)"
                :value="node.data?.[col.field]"
                :response-type="columnDefsMap.get(col.field)?.responseType"
                :is-file-node="!!node.data?.contractUid"
              />
            </template>

            <!-- One line, ellipsised. A cell that wraps drags the whole row
                 taller, and one long "Contractual Parties" list would then set the
                 height for every column beside it. The full text is on the
                 title, and the extraction modal shows it in full. -->
            <span
              v-else
              class="cell-value"
              :class="[
                getColumnValueClass(node, col.field),
                { blank: isBlankCell(node, col.field) },
              ]"
              :title="getColumnDisplayValue(node, col.field)"
            >
              {{ getColumnDisplayValue(node, col.field) }}
            </span>
          </div>
        </template>
      </Column>
      <template #empty>
        <div class="grid-empty-state">
          <div class="grid-empty-state-content">
            <div class="grid-empty-state-icon">🔍</div>
            <span class="grid-empty-state-text">No Data</span>
          </div>
        </div>
      </template>
    </TreeTable>

    <!-- Cell panel: the value in full, then what can be done with it. Cells
         are a single ellipsised line, so this is where the whole value lives. -->
    <div
      v-if="contextMenuVisible"
      ref="contextMenuRef"
      class="cell-panel"
      :style="{ left: `${contextMenuPosition.x}px`, top: `${contextMenuPosition.y}px` }"
      @click.stop
    >
      <div class="cell-panel__head">
        <span class="cell-panel__label">{{ panelColumnLabel }}</span>
        <p class="cell-panel__value" :class="{ blank: panelValueIsBlank }">{{ panelValue }}</p>
      </div>

      <div class="cell-panel__rule" />

      <!-- Why a cell can't be edited is worth saying, so the reason takes the
           place of the action rather than hiding in a tooltip. -->
      <div v-if="!isEditEnabled && editDisabledTooltip" class="cell-panel__note">
        <svg
          width="13"
          height="13"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          stroke-width="1.3"
          :style="{ flex: 'none' }"
        >
          <rect x="2.5" y="5.5" width="7" height="5" rx="1" />
          <path d="M4 5.5V4a2 2 0 0 1 4 0v1.5" />
        </svg>
        <span>{{ editDisabledTooltip }}</span>
      </div>

      <button v-else type="button" class="cell-panel__item" @click="handleEditAction">
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M3 13.5 12 4.5l-1-1L2 12.5l-.4 1.4z" />
        </svg>
        {{ isStaticColumnCell ? 'Edit assigned entities' : 'Edit value' }}
      </button>

      <button type="button" class="cell-panel__item" @click="handleGoToContractAction">
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M9.5 2.5h4v4M13.5 2.5 7.5 8.5" />
          <path d="M12 9.5v3a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3" />
        </svg>
        {{ isContractFileRow ? 'Go to contract file' : 'Go to contract' }}
      </button>

      <button
        v-if="isContractFileRow && hasVaultFile"
        type="button"
        class="cell-panel__item"
        @click="handleAddRevisionAction"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="8" cy="8" r="6" />
          <path d="M8 4.6V8l2.2 1.4" />
        </svg>
        Add revision
      </button>
    </div>
  </div>
</template>

<style scoped>
.grid-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

/* Reset TD padding */
:deep(.excel-tree-table .p-treetable-tbody > tr > td) {
  padding: 0 !important;
}

:deep(.p-treetable .p-treetable-tbody > tr > td .p-treetable-toggler) {
  margin-right: 4px;
  margin-left: 8px;
}

.cell-wrapper {
  width: 100%;
  height: 100%;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  cursor: default;
  transition: background-color 0.1s;
  /* min-width is what actually lets the ellipsis happen: without it a flex
     child refuses to shrink below its content. */
  min-width: 0;
  min-height: 42px;
  font-size: v-bind(cellSize);
  color: v-bind(cellColor);
}

/* Every value is one ellipsised line — see the note in the template. */
.cell-value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* One step lighter than muted text: this marks absence, not de-emphasis. */
.cell-value.blank {
  color: v-bind(blankColor);
}

/* Ink, not accent: the ring says "this is the cell you're on", which is a
   position rather than a state worth colouring. */
.cell-selected {
  box-shadow: inset 0 0 0 2px v-bind(inkColor);
  background-color: v-bind(accentSurface);
  z-index: 1;
}

.cell-editing {
  background-color: v-bind(surface);
  box-shadow: inset 0 0 0 2px v-bind(accent);
}

.cell-readonly {
  justify-content: center;
}

/* General Styling */
:deep(.excel-tree-table.p-treetable) {
  /* PrimeVue's table sets its own family; pull it back to the app font. */
  font-family: var(--font-family);
  font-size: v-bind(cellSize);
}
:deep(.p-treetable .p-treetable-header) {
  padding: 0;
  border-bottom: 1px solid v-bind(headBorder);
}

/* The header is a tinted strip of dense caps — same treatment as GaiaDataTable,
   so the two tables in Contract Management read as one family. */
:deep(.p-treetable .p-treetable-thead > tr > th) {
  background: v-bind(headSurface);
  color: v-bind(headColor);
  font-weight: v-bind(headWeight);
  font-size: v-bind(headSize);
  letter-spacing: v-bind(headTracking);
  text-transform: uppercase;
  padding: 9px 12px;
  border-right: 1px solid v-bind(cellBorder);
  border-bottom: 1px solid v-bind(headBorder);
  white-space: nowrap;
  overflow: hidden;
}

/* Header text container - ensures proper truncation */
.treetable-header-text {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

/* The expander column's values clear the toggler — a 25px button plus its 4px
   margin — so they sit 29px further in than any other column's. Without the
   same offset the label reads as belonging to the column on its left rather
   than to the names under it. Keyed off `col.expander` so the indent follows
   the toggler if the expander moves to another column. */
.treetable-header-text.expander-indent {
  padding-left: 29px;
}

/* Sort affordance — the same hover/active colours GaiaDataTable gives its own
   header, so the two Contract Management tables sort alike. */
.treetable-header-text.sortable {
  cursor: pointer;
}

.treetable-header-text.sortable:hover {
  color: v-bind(cellColor);
}

.treetable-header-text.sorted {
  color: v-bind(inkColor);
}

/* One step back from the header label, so the glyph reads as an offer rather
   than as part of the column's name. Hover and the active sort bring it
   forward. */
.sort-caret {
  color: v-bind(faintColor);
}

.treetable-header-text.sortable:hover .sort-caret:not(.active) {
  color: v-bind(cellColor);
}

.sort-caret.active {
  color: v-bind(inkColor);
}

/* Header title - truncates with ellipsis */
.treetable-header-title {
  display: inline-block;
}

/* Header filter icon/badge - always visible, never shrinks */
.treetable-header-filter {
  flex-shrink: 0 !important;
}

:deep(.p-treetable .p-treetable-tbody > tr > td) {
  border-right: 1px solid v-bind(cellBorder);
  border-bottom: 1px solid v-bind(cellBorder);
}
:deep(.p-treetable .p-treetable-tbody > tr:hover) {
  background-color: v-bind(rowHover);
}

/* PrimeVue's scrollable rows are flex boxes pinned to `width: 100%` — the
   *visible* width, not the scrolled width — while the cells carry on past the
   fold. Anything painted on the row itself therefore stops mid-table, which is
   why the hover tint cut off before the last columns. Sizing the row to its
   content puts the highlight under every column; min-width keeps it filling the
   table when the columns don't reach the edge. */
:deep(.p-treetable-scrollable .p-treetable-tbody > tr) {
  width: max-content;
  min-width: 100%;
}

/* The frozen column is `position: sticky; background: inherit`, so on hover it
   inherits the row's translucent tint and the cells scrolling underneath show
   straight through it. An opaque base makes it solid again; the hover tint is
   layered over that base rather than replacing it, so the cell still matches
   the rest of the row exactly instead of needing a pre-blended colour. */
:deep(.p-treetable .p-treetable-tbody > tr > td.p-frozen-column) {
  background: v-bind(surface);
  z-index: 1;
}

:deep(.p-treetable .p-treetable-tbody > tr:hover > td.p-frozen-column) {
  background-image: linear-gradient(v-bind(rowHover), v-bind(rowHover));
}

.row-number-header,
:deep(.row-number-cell) {
  color: v-bind(faintColor);
  font-size: v-bind(microSize);
  font-variant-numeric: tabular-nums;
}

:deep(.status-pill) {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  border: 1px solid transparent;
}
:deep(.status-active) {
  background-color: #ecfdf5;
  color: #047857;
  border-color: #d1fae5;
}
:deep(.status-prospect) {
  background-color: #eff6ff;
  color: #1d4ed8;
  border-color: #dbeafe;
}
:deep(.status-churned) {
  background-color: #f9fafb;
  color: #4b5563;
  border-color: #e5e7eb;
}
:deep(.status-trial) {
  background-color: #faf5ff;
  color: #7e22ce;
  border-color: #f3e8ff;
}

/* The trailing "+" reads as a control, not a column label — so it drops the
   header's caps treatment and centres in its narrow track. */
:deep(.add-column-header) {
  cursor: pointer;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: v-bind(headWeight);
  color: v-bind(faintColor);
}

:deep(.add-column-header:hover) {
  color: v-bind(inkColor);
}

.cell-editor-input {
  width: 100%;
  border: none;
  outline: none;
  font: inherit;
  padding: 0;
  background: transparent;
}

/* Cell panel — the value in full plus its actions. Anchored under the cell,
   fixed so it escapes the grid's own scroll containers. */
.cell-panel {
  position: fixed;
  z-index: 10000;
  width: 300px;
  max-width: calc(100vw - 24px);
  padding: 6px;
  border-radius: 12px;
  background: v-bind(surface);
  box-shadow:
    0 1px 2px rgba(25, 27, 31, 0.06),
    0 12px 32px rgba(25, 27, 31, 0.14);
  animation: fadeIn 0.1s ease-out;
}

.cell-panel__head {
  padding: 8px 10px 10px;
}

.cell-panel__label {
  display: block;
  font-size: v-bind(headSize);
  font-weight: v-bind(headWeight);
  letter-spacing: v-bind(headTracking);
  text-transform: uppercase;
  color: v-bind(headColor);
}

/* The one place the value is allowed to wrap — capped so a long list can't
   push the actions off the screen. */
.cell-panel__value {
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: v-bind(inkColor);
  max-height: 132px;
  overflow-y: auto;
  overflow-wrap: anywhere;
}

.cell-panel__value.blank {
  color: v-bind(blankColor);
}

.cell-panel__rule {
  height: 1px;
  margin: 4px 4px 5px;
  background: v-bind(cellBorder);
}

.cell-panel__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border: none;
  border-radius: 7px;
  background: none;
  font-family: inherit;
  font-size: v-bind(cellSize);
  color: v-bind(inkColor);
  text-align: left;
  cursor: pointer;
}

.cell-panel__item:hover {
  background: v-bind(rowHover);
}

.cell-panel__item.danger {
  color: v-bind(alertColor);
}

.cell-panel__item.danger:hover {
  background: v-bind(alertSurface);
}

/* Why an action is unavailable, in place of the action itself. */
.cell-panel__note {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 4px 10px 8px;
  font-size: v-bind(cellSize);
  line-height: 1.45;
  color: v-bind(faintColor);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Visual distinction for "most recent" values */
:deep(.most-recent-value) {
  font-weight: 600 !important;
  color: #2080f0 !important;
}

/* Faded values for contract files that have the field but it's not the most recent */
:deep(.faded-value) {
  opacity: 0.6 !important;
}

/* Empty state styling for grid */
.grid-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 48px 24px;
  width: 100%;
}

.grid-empty-state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 12px;
}

.grid-empty-state-icon {
  font-size: 48px;
  opacity: 0.6;
}

.grid-empty-state-text {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

/* Style the empty message row in PrimeVue TreeTable */
:deep(.p-treetable-tbody .p-treetable-emptymessage) {
  text-align: center;
  padding: 48px 24px;
}

:deep(.p-treetable-tbody .p-treetable-emptymessage td) {
  border: none;
  padding: 0;
  text-align: center;
}
</style>
